import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { JournalPostSummary } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getPosts } from '@/lib/data/journal'
import { formatShortDate } from '@/lib/format'
import { useEntrance } from '@/components/motion/reveal'
import ScrambleText from '@/components/motion/scramble-text'
import StoryRow from '@/components/journal/story-row'
import Img from '@/components/generals/img'
import { media } from '@/constants/media'

interface JournalProps {
    posts: Array<JournalPostSummary>
}

const Journal: FC<JournalProps> = ({ posts = [] }): ReactNode => {
    const header = useRef<HTMLElement>(null)
    const [featured, ...rest] = posts

    useEntrance(header, timeline => {
        timeline
            .from('[data-jr-meta]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
            .from('[data-jr-title]', { yPercent: 105, duration: 1.4, ease: 'expo.out' }, 0.05)
            .from('[data-jr-cover]', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.5, ease: 'expo.inOut' }, 0.25)
            .from('[data-jr-cover] img', { scale: 1.3, duration: 2, ease: 'expo.out' }, 0.25)
            .from('[data-jr-text] > *', { autoAlpha: 0, y: 24, duration: 1, stagger: 0.08 }, 0.6)
    })

    return (
        <Layout>
            <Seo
                title='Journal'
                description="Notes from the Elio studio: essays, atelier stories, store openings and care guides for garments made to last."
                ogImage={featured?.cover || media.concreteRoomBlack}
            />

            <section ref={header} aria-labelledby='journal-title' className='pt-[calc(var(--header-height)+48px)] gutter md:pt-[calc(var(--header-height)+72px)]'>
                <p data-jr-meta className='label-mono'>
                    (Journal) — Notes from the studio
                </p>

                <div className='overflow-hidden pb-[0.06em]'>
                    <h1 id='journal-title' data-jr-title className='mt-6 font-heading text-[clamp(60px,11vw,184px)] leading-[0.84] text-bone'>
                        <ScrambleText text='The Journal' trigger='enter' />
                    </h1>
                </div>

                {featured ? (
                    <article className='mt-12 grid gap-8 md:mt-16 md:grid-cols-12 md:items-end md:gap-10'>
                        <Link
                            href={`/journal/${featured.slug}`}
                            data-cursor='Read'
                            data-transition-label={featured.title}
                            className='group md:col-span-7'
                            aria-label={`Read ${featured.title}`}
                        >
                            <div data-jr-cover className='relative aspect-[6/5] overflow-hidden bg-coal'>
                                <Img
                                    src={featured.cover}
                                    alt={featured.cover_alt || featured.title}
                                    sizes='(min-width: 768px) 58vw, 100vw'
                                    preload
                                    className='transition-transform duration-[1.4s] ease-expo-out group-hover:scale-[1.04]'
                                />
                            </div>
                        </Link>

                        <div data-jr-text className='md:col-span-5'>
                            <p className='flex items-center gap-3 meta-mono'>
                                <span className='bg-acid px-2 py-1 text-ink'>
                                    Latest
                                </span>

                                <span className='text-bone' data-mdlfy-cms={`journal_posts:${featured.id}:category`}>{featured.category}</span>
                            </p>

                            <h2 className='mt-6 display-m uppercase'>
                                <Link
                                    href={`/journal/${featured.slug}`}
                                    data-transition-label={featured.title}
                                    className='transition-colors duration-300 hover:text-acid'
                                >
                                    <span data-mdlfy-cms={`journal_posts:${featured.id}:title`}>{featured.title}</span>
                                </Link>
                            </h2>

                            {featured.excerpt ? (
                                <p className='mt-5 max-w-[44ch] lead text-bone/80'>
                                    <span data-mdlfy-cms={`journal_posts:${featured.id}:excerpt`}>{featured.excerpt}</span>
                                </p>
                            ) : null}

                            <div className='mt-8 flex items-center justify-between gap-4 border-t border-line pt-4 meta-mono text-ash'>
                                {featured.author ? <span data-mdlfy-cms={`journal_posts:${featured.id}:author`}>{featured.author}</span> : <span>Elio Studio</span>}

                                <time dateTime={featured.published_on}>
                                    {formatShortDate(featured.published_on)}
                                </time>
                            </div>
                        </div>
                    </article>
                ) : (
                    <p className='mt-12 max-w-[48ch] lead text-bone/70'>
                        The first stories are still being written. Check back soon.
                    </p>
                )}
            </section>

            {rest.length ? (
                <section aria-label='More stories' className='pb-28 pt-20 gutter md:pb-40 md:pt-28'>
                    <ul className='border-t border-line'>
                        {rest.map((post, index) => (
                            <StoryRow key={post.id} post={post} index={index} />
                        ))}
                    </ul>
                </section>
            ) : null}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<JournalProps> = async () => {
    try {
        return { props: { posts: await getPosts() } }
    } catch (error) {
        console.error('Journal stories could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { posts: [] } }
    }
}

export default Journal
